import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Code2,
  Brain,
  Globe,
  Shield,
  Cloud,
  Lightbulb
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

const ServicesPreview = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const { t } = useTranslation()

  const services = [
    {
      icon: Code2,
      title: t('services.customSoftware.title'),
      description: t('services.customSoftware.description'),
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Brain,
      title: t('services.ai.title'),
      description: t('services.ai.description'),
      gradient: "from-violet-500 to-purple-500"
    },
    {
      icon: Globe,
      title: t('services.web.title'),
      description: t('services.web.description'),
      gradient: "from-cyan-500 to-teal-500"
    },
    {
      icon: Shield,
      title: t('services.cybersecurity.title'),
      description: t('services.cybersecurity.description'),
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: Cloud,
      title: t('services.cloud.title'),
      description: t('services.cloud.description'),
      gradient: "from-indigo-500 to-blue-500"
    },
    {
      icon: Lightbulb,
      title: t('services.consulting.title'),
      description: t('services.consulting.description'),
      gradient: "from-amber-500 to-orange-500"
    }
  ]

  return (
    <section ref={ref} className="section-padding bg-gray-50">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('services.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('services.description')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 card-hover h-full">
                  <div className={`w-16 h-16 bg-gradient-to-r ${service.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-primary-600 transition-colors">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  
                  <Link 
                    to="/services" 
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors group-hover:translate-x-1 transform duration-300"
                  >
                    {t('services.details')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </motion.div>
            )
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="text-center mt-12"
        >
          <Link to="/services" className="btn-primary">
            {t('services.cta')}
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

export default ServicesPreview
